import { useState } from 'react'
import { Skill } from '@/assets/icons'
import { InlineTextButton } from '@/components/inline-text-button/InlineTextButton'
import { useAppSelector } from '@/store/store-hooks'
import getUserSkill from '@/user-skills/fetch/get-user-skill'
import { useUserSkillMutations } from '@/user-skills/hooks/useUserSkillMutations'
import type { UserSkill } from '@/user-skills/schemas'
import UserSkillEditor from './UserSkillEditor'

export default function SavedUserSkills({ conversationId, turnId }: { conversationId: string; turnId: string }) {
  const entries = useAppSelector((state) => state.userSkillChatState.byConversationId[conversationId])
  return entries
    ?.filter((entry) => entry.turnId === turnId)
    .map((entry) => <SavedSkillLink key={entry.eventId} skillId={entry.skillId} skillName={entry.skillName} />)
}

function SavedSkillLink({ skillId, skillName }: { skillId: string; skillName: string }) {
  const { saveSkill } = useUserSkillMutations()
  const [editingSkill, setEditingSkill] = useState<UserSkill | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const open = async () => {
    if (loading) return
    setLoading(true)
    setLoadError(false)
    try {
      setEditingSkill(await getUserSkill(skillId))
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }
  return (
    <div className="mb-2 whitespace-normal text-sm leading-6">
      <p>
        Saved{' '}
        <InlineTextButton disabled={loading} onClick={() => void open()}>
          <Skill className="mr-1 inline size-4 align-text-bottom" aria-hidden />
          {skillName}
        </InlineTextButton>
        .
      </p>
      {loadError && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          Couldn’t open your skill. It may have been deleted. Try again.
        </p>
      )}
      {editingSkill && (
        <UserSkillEditor skill={editingSkill} skillId={skillId} onClose={() => setEditingSkill(null)} onSave={(content) => saveSkill(skillId, content)} />
      )}
    </div>
  )
}
